import { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import { specialsList } from "@/mocks/specials";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Special() {
  usePageMeta(
    "专题合集 - 91动漫",
    "91动漫专题合集，按主题整理动漫片单与资讯，涵盖新番前瞻、经典回顾、国漫崛起与科幻机甲等特色内容。",
    "91动漫,专题合集,动漫专题,动漫片单,新番前瞻"
  );

  const [openId, setOpenId] = useState<string>(specialsList[0]?.id ?? "");

  const featured = specialsList[0];

  return (
    <div className="w-full">
      <PageHero
        title="专题合集"
        description="围绕特定主题整理的动漫片单与资讯合集，让你更有方向地追番、回顾与探索。"
        crumbs={[{ label: "专题合集" }]}
        icon="ri-stack-line"
      />

      {featured && (
        <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative h-56 w-full lg:h-full">
                <img
                  src={featured.cover}
                  alt={`${featured.title} - 91动漫专题`}
                  title={`${featured.title} - 91动漫`}
                  className="h-full w-full object-top object-cover"
                />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-white">
                  <i className="ri-fire-line" />
                  本季主推专题
                </span>
              </div>
              <div className="p-6 md:p-8">
                <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{featured.intro}</p>
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-accent-50 p-4">
                  <i className="ri-lightbulb-line mt-0.5 text-lg text-accent-600" />
                  <p className="text-sm leading-relaxed text-accent-900">
                    <strong>推荐理由：</strong>
                    {featured.reason}
                  </p>
                </div>
                <div className="mt-5 flex flex-col gap-4">
                  {featured.groups.map((g) => (
                    <div key={g.name}>
                      <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                        <span className="h-4 w-1 rounded-full bg-primary-500" />
                        {g.name}
                      </h3>
                      <p className="mt-1 pl-3 text-xs text-foreground-500">{g.desc}</p>
                      <div className="mt-2 flex flex-col gap-2 pl-3">
                        {g.entries.map((e) => (
                          <Link
                            key={e.title}
                            to={e.link}
                            className="group flex items-center justify-between gap-3 rounded-md border border-transparent bg-background-100 px-3 py-2.5 transition-colors hover:border-accent-300 hover:bg-accent-50"
                          >
                            <span className="min-w-0">
                              <span className="line-clamp-1 block text-sm font-medium text-foreground-800 group-hover:text-accent-700">
                                {e.title}
                              </span>
                              <span className="line-clamp-1 block text-xs text-foreground-500">{e.desc}</span>
                            </span>
                            <i className="ri-arrow-right-line shrink-0 text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-accent-600" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="border-y border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
          <div className="flex items-center gap-2">
            <i className="ri-folders-line text-xl text-primary-600" />
            <h2 className="font-heading text-2xl font-extrabold text-foreground-950">全部专题</h2>
          </div>
          <p className="mt-2 text-sm text-foreground-500">点击专题标题展开分组片单，专题内条目可直接跳转到对应内容。</p>

          <div className="mt-6 flex flex-col gap-4">
            {specialsList.slice(1).map((s) => {
              const open = openId === s.id;
              return (
                <div
                  key={s.id}
                  className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? "" : s.id)}
                    className="flex w-full cursor-pointer items-center gap-4 p-4 text-left md:p-5"
                  >
                    <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md md:h-20 md:w-32">
                      <img
                        src={s.cover}
                        alt={`${s.title} - 91动漫专题`}
                        title={`${s.title} - 91动漫`}
                        className="h-full w-full object-top object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-heading text-lg font-bold text-foreground-950">{s.title}</h3>
                      <p className="line-clamp-2 mt-1 text-sm text-foreground-500">{s.intro}</p>
                    </div>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className={open ? "ri-subtract-line text-lg" : "ri-add-line text-lg"} />
                    </span>
                  </button>

                  {open && (
                    <div className="border-t border-background-100 bg-background-100/50 p-4 md:p-6 animate-fade-in">
                      <div className="flex items-start gap-2 rounded-lg bg-accent-50 p-4">
                        <i className="ri-lightbulb-line mt-0.5 text-lg text-accent-600" />
                        <p className="text-sm leading-relaxed text-accent-900">
                          <strong>推荐理由：</strong>
                          {s.reason}
                        </p>
                      </div>
                      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                        {s.groups.map((g) => (
                          <div key={g.name}>
                            <h4 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                              <span className="h-4 w-1 rounded-full bg-primary-500" />
                              {g.name}
                            </h4>
                            <p className="mt-1 pl-3 text-xs text-foreground-500">{g.desc}</p>
                            <div className="mt-2 flex flex-col gap-2 pl-3">
                              {g.entries.map((e) => (
                                <Link
                                  key={e.title}
                                  to={e.link}
                                  className="group flex items-center justify-between gap-3 rounded-md bg-background-50 px-3 py-2.5 transition-colors hover:bg-accent-50"
                                >
                                  <span className="min-w-0">
                                    <span className="line-clamp-1 block text-sm font-medium text-foreground-800 group-hover:text-accent-700">
                                      {e.title}
                                    </span>
                                    <span className="line-clamp-1 block text-xs text-foreground-500">{e.desc}</span>
                                  </span>
                                  <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-700">
                                    {e.type}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">还想看点别的？</h3>
            <p className="mt-1 text-sm text-foreground-500">
              浏览动漫资讯或作品推荐，发现更多值得一看的内容。
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-newspaper-line" />
              动漫资讯
            </Link>
            <Link
              to="/recommend"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
            >
              <i className="ri-movie-2-line" />
              作品推荐
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}